import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    ADD, EDIT, IDLE, PENDING, YES, ACTIVE,
} from "const"
import { StateType, hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_SIL_SUBSYSTEM,
    StoreType,
    SilDeviceNestedType,
    SilSubsystemProps,

    LoadSilDeviceById,
    LoadAllSilDevices,
    LoadAllQidProductsWithModels,
    LoadAllQidCompanies,
    LoadQidDatapoints,
    PrepareResultDatapoints,

    setSilDeviceNestedCurrentList,
    setSilDeviceNestedNewList,
    setSilDeviceEdit,
    setSilDeviceReloadEdit,

    setSilModelDevicesAccountDeviceList,
    setSilModelDevicesAccountSubsystemList,
    setSilModelDevicesProjectDeviceList,
    setSilModelDevicesProjectSubsystemList,
    setSilModelDevicesQidProductList,
    setSilModelDevicesQidDatapointList,
    setSilModelDevicesQidClearExpanded,
    setSilModelDevicesQidClearDatapointList,
    setQidCompanyList,

    initSilDevice,
    initSilProjectDevice,
    moduleIds,
} from "client/store"

import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
} from "client/components"

import NotFound from "client/parts/notFound"
import AccessDenied from "client/parts/accessDenied"
import Form from "./form"

const SubsystemEdit = ({
    subsystemId = null,
}: SilSubsystemProps) => {
    const project = useSelector((state: StoreType) => state.project.projectView)

    const subsystem = useSelector((state: StoreType) => state.silDevice.deviceEdit)
    const reloadEdit = useSelector((state: StoreType) => state.silDevice.reloadEdit)

    const packageSettings = useSelector((state: StoreType) => state.system.packageSettings)
    const moduleList = useSelector((state: StoreType) => state.module.moduleList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)

    const reload = () => {
        dispatch(setSilDeviceReloadEdit(false))
        setTimeout(() => {
            dispatch(setSilDeviceReloadEdit(true))
        }, 0)
    }

    useEffect(() => {
        // load available devices
        const promiseArray: Promise<any>[] = [
            // published account devices
            LoadAllSilDevices(userData.accountId, null, false, true).then((res) => {
                dispatch(setSilModelDevicesAccountDeviceList(res))
            }),
        ]
        if (project.id) {
            promiseArray.push(
                // published account subsystems
                LoadAllSilDevices(userData.accountId, null, true, true).then((res) => {
                    dispatch(setSilModelDevicesAccountSubsystemList(res))
                }),
                // published project devices
                LoadAllSilDevices(userData.accountId, project.id, false, true).then((res) => {
                    dispatch(setSilModelDevicesProjectDeviceList(res))
                }),
                // published project subsystems
                LoadAllSilDevices(userData.accountId, project.id, true, true).then((res) => {
                    // disable select the subsystem as nested of itself
                    const filteredList = { ...res }
                    delete filteredList[subsystemId]

                    dispatch(setSilModelDevicesProjectSubsystemList(filteredList))
                }),
            )
        }

        const backgroundPromiseArray: Promise<any>[] = []

        const { qidModuleAccess } = packageSettings
        const moduleAccess = qidModuleAccess === YES && moduleList[moduleIds.qid].status === ACTIVE
        const qidAccess = userData.accountId === 1 || moduleAccess
        if (qidAccess) {
            promiseArray.push(
                // get all published products with published models
                LoadAllQidProductsWithModels().then((productList) => {
                    dispatch(setSilModelDevicesQidProductList(productList))

                    Object.values(productList).forEach((product) => {
                        backgroundPromiseArray.push(
                            LoadQidDatapoints({ productId: product.id }).then((res) => {
                                const datapoints = PrepareResultDatapoints(res)
                                dispatch(setSilModelDevicesQidDatapointList(product.id, datapoints))
                            }),
                        )
                    })
                }),
                LoadAllQidCompanies(true).then((res) => { // used in search
                    dispatch(setQidCompanyList(res))
                }),
            )
        }

        // load subsystem data
        if (subsystemId === 0) {
            const initDevice = project.id ? initSilProjectDevice : initSilDevice
            dispatch(setSilDeviceEdit({
                ...initDevice,
                nested: true,
            }))

            const emptyNestedList: SilDeviceNestedType[] = [null, null, null]
            dispatch(setSilDeviceNestedCurrentList(emptyNestedList))
            dispatch(setSilDeviceNestedNewList(emptyNestedList))
        } else {
            promiseArray.push(
                LoadSilDeviceById(subsystemId, userData.accountId, project.id).then((res) => {
                    dispatch(setSilDeviceEdit(res))
                    dispatch(setSilDeviceNestedCurrentList(res.nestedDevices))
                    dispatch(setSilDeviceNestedNewList(res.nestedDevices))
                }),
            )
        }


        setLoadState(PENDING)
        Promise.all(promiseArray).then(() => {
            setLoadState(IDLE)
            reload()

            Promise.all(backgroundPromiseArray)
        })

        // clear available model devices on unmount
        return () => {
            dispatch(setSilModelDevicesAccountDeviceList(null))
            dispatch(setSilModelDevicesAccountSubsystemList(null))
            dispatch(setSilModelDevicesProjectDeviceList(null))
            dispatch(setSilModelDevicesProjectSubsystemList(null))

            dispatch(setSilModelDevicesQidProductList(null))
            dispatch(setSilModelDevicesQidClearExpanded())
            dispatch(setSilModelDevicesQidClearDatapointList())
        }
    }, [])

    const addAccess = !project.id
        ? hasAccess(PRM_SIL_SUBSYSTEM, ADD)
        : hasProjectAccess(PRM_SIL_SUBSYSTEM, ADD)

    const editAccess = !project.id
        ? hasAccess(PRM_SIL_SUBSYSTEM, EDIT)
        : hasProjectAccess(PRM_SIL_SUBSYSTEM, EDIT)

    if ((subsystemId === 0) && !addAccess) return <AccessDenied />
    if ((subsystemId !== 0) && !editAccess) return <AccessDenied />
    if (!subsystem) return <NotFound />

    return (
        <Container>
            <PageHeader>
                <PageTitle>
                    { subsystem.id ? `${lang.editSubsystem} # ${subsystem.id}` : lang.addSubsystem }
                </PageTitle>
            </PageHeader>
            <PageContent>
                {loadState === PENDING && <Loader />}
                {
                    !reloadEdit
                    && <Form subsystem={subsystem} />
                }
                {
                    reloadEdit
                    && <Form subsystem={subsystem} />
                }
            </PageContent>
        </Container>
    )
}
export default SubsystemEdit
